import type { Embed } from '../types/embed'

/**
 * **The end of an asset with nothing to play through** — an image, a document.
 *
 * A video or an audio track ends when its playback does. An image or a PDF has no playback, so its
 * `ended` is a moment of its viewing: **an image once it is displayed**, **a document once its last
 * page is reached** — paged or scrolled to, or on screen from the start when it has one page. A page
 * that framed the player and completes a step on `ended` (`entries/players/main.ts`,
 * `emitParentEvent`) can then complete one whose entry is a picture or a PDF.
 *
 * **It is said of one asset played alone, once per load.** A gallery of several images or a list
 * of documents has no single end; and inside a collection an entry's `ended` moves the collection
 * on, which an image ending the moment it shows would do at once — so a collection's shell does not
 * listen to these (`players/collection/CollectionEmbed.vue`), and neither does the end card a
 * channel's framed item draws once a playback is over (`players/embed/external/ExternalEmbed.vue`).
 */

/** The types whose `ended` is a moment of their viewing rather than the end of a playback. */
export const STILL_TYPES: readonly string[] = ['image', 'document']

/** Whether an asset of this type ends on being viewed — an image, a document — rather than played. */
export function isStill(type: string | null | undefined): boolean {
  return type != null && STILL_TYPES.includes(type)
}

/** Whether an embed is one asset on its own: the only kind an image's or a document's `ended` is said of. */
export function isSingleAsset(embed: Pick<Embed, 'embedType' | 'items'> | null | undefined): boolean {
  return embed != null && embed.embedType !== 'collection' && embed.items.length === 1
}

/**
 * **A reporter that says `ended` once per load.** The function it answers calls `onEnded` the first
 * time it is handed `load` — the embed that was read — and does nothing when handed it again, so a
 * picture drawn again at another size, or after a trip to fullscreen, does not end it twice. A new
 * load is a new object, and ends once in its turn.
 */
export function endOncePerLoad(onEnded: () => void): (load: object) => void {
  let ended: object | undefined
  return (load) => {
    if (load === ended) return
    ended = load
    onEnded()
  }
}
